import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Star, ShoppingCart, Check, ShieldCheck, Clock, BadgeCheck, Minus, Plus, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';

// Hours the customer can pick when a service is priced hourly.
// Linear pricing: total = hourly_rate × hours (no tiered discounts — per user request 2026-02).
const HOUR_OPTIONS_LINEAR = [1, 2, 3, 4, 5, 6, 8];

// Legacy tiered options kept ONLY for services that still use the old basis='Hourly'
// flag without the new pricing_type='hourly' setup. New services should set
// pricing_type='hourly' + hourly_rate in the admin panel.
const HOUR_OPTIONS = [
  { hours: 1, factor: 1.0,  save: 0  },
  { hours: 2, factor: 1.85, save: 8  },
  { hours: 3, factor: 2.65, save: 12 },
  { hours: 4, factor: 3.4,  save: 15 },
  { hours: 5, factor: 4.15, save: 17 },
  { hours: 6, factor: 4.8,  save: 20 },
  { hours: 8, factor: 6.24, save: 22 },
];

const computeHourlyPrice = (base, hours) => {
  const opt = HOUR_OPTIONS.find((o) => o.hours === hours) || HOUR_OPTIONS[0];
  return Math.round(base * opt.factor);
};

const ServiceDetail = () => {
  const { categoryId, serviceId } = useParams();
  const nav = useNavigate();
  const { services, cart, addToCart, updateQty } = useApp();
  const data = services[categoryId] || { title: 'Services', items: [] };
  const item = data.items.find((i) => i.id === serviceId);
  const [tab, setTab] = useState('overview');
  const [selectedHours, setSelectedHours] = useState(1);

  if (!item) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <DesktopHeader />
        <div className="max-w-[900px] mx-auto px-5 py-16 text-center">
          <p className="text-neutral-600">Service not found</p>
          <button onClick={() => nav(`/services/${categoryId}`)} className="mt-3 text-[#2E7D32] font-semibold">← Back to {data.title}</button>
        </div>
      </div>
    );
  }

  // Detect hourly pricing: new services use pricing_type='hourly' + hourly_rate,
  // legacy services fall back to basis='Hourly' with the tiered HOUR_OPTIONS table.
  const isNewHourly    = item.pricing_type === 'hourly' && Number(item.hourly_rate) > 0;
  const isLegacyHourly = !isNewHourly && item.basis === 'Hourly';
  const isHourly       = isNewHourly || isLegacyHourly;

  const minHours = isNewHourly ? Math.max(1, Number(item.min_hours) || 1) : 1;
  const configuredAllowed = Array.isArray(item.allowed_hours) && item.allowed_hours.length ? item.allowed_hours : null;
  const hourChoices = isNewHourly
    ? (configuredAllowed
        ? configuredAllowed.filter((h) => h >= minHours)
        : HOUR_OPTIONS_LINEAR.filter((h) => h >= minHours))
    : HOUR_OPTIONS.map((o) => o.hours);

  // Force selectedHours to a valid choice
  const effectiveHours = hourChoices.includes(selectedHours) ? selectedHours : hourChoices[0];

  const finalPrice = isNewHourly
    ? Number(item.hourly_rate) * effectiveHours
    : (isLegacyHourly ? computeHourlyPrice(item.price, effectiveHours) : item.price);

  const perHourSavePct = isLegacyHourly
    ? (HOUR_OPTIONS.find((o) => o.hours === effectiveHours)?.save || 0)
    : 0;

  const cartItemId = isHourly ? `${item.id}__${effectiveHours}h` : item.id;
  const inCart = cart.find((c) => c.id === cartItemId);

  const handleAdd = () => {
    if (isHourly) {
      addToCart({
        ...item,
        id: cartItemId,
        name: `${item.name} (${effectiveHours}h)`,
        price: finalPrice,
        hours: effectiveHours,
      });
    } else {
      addToCart(item);
    }
  };

  const relatedItems = data.items.filter((i) => i.id !== serviceId).slice(0, 4);

  const highlights = [
    { icon: ShieldCheck, label: '100% Guarantee', desc: 'Not happy? Re-clean free' },
    { icon: Clock, label: 'On-time arrival', desc: 'Or your money back' },
    { icon: BadgeCheck, label: 'Verified pros', desc: 'Background checked' },
  ];

  const included = [
    'Full service by trained professional',
    'All cleaning supplies & equipment included',
    'Live tracking of your service',
    'Safe & eco-friendly products',
  ];

  const excluded = [
    'Any external material required',
    'Movement of heavy furniture',
    'Additional add-on services',
  ];

  const reviews = [
    { id: 'r1', name: 'Priya S.', rating: 5, comment: 'Excellent service! Very professional and thorough.', date: '2 days ago' },
    { id: 'r2', name: 'Rahul M.', rating: 5, comment: 'On time and very courteous. Will book again.', date: '1 week ago' },
    { id: 'r3', name: 'Anita K.', rating: 4, comment: 'Good service, small issue with timing but quickly resolved.', date: '2 weeks ago' },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 pb-28 md:pb-8">
      <DesktopHeader />

      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-100">
        <div className="px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-base font-extrabold truncate">{item.name}</h1>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-6 pt-4 md:pt-6">
        <div className="hidden md:flex items-center gap-2 text-sm text-neutral-500 mb-4">
          <button onClick={() => nav('/home')} className="hover:text-neutral-900">Home</button>
          <ChevronRight className="w-3 h-3" />
          <button onClick={() => nav(`/services/${categoryId}`)} className="hover:text-neutral-900">{data.title}</button>
          <ChevronRight className="w-3 h-3" />
          <span className="text-neutral-900 font-medium">{item.name}</span>
        </div>

        <div className="grid md:grid-cols-5 gap-5">
          {/* LEFT */}
          <div className="md:col-span-3 space-y-4">
            <div className="relative rounded-2xl overflow-hidden bg-white border border-neutral-200">
              <img src={item.image} alt={item.name} className="w-full h-64 md:h-96 object-cover" />
              <div className="absolute top-3 left-3 bg-white/95 backdrop-blur text-[#2E7D32] text-xs font-bold px-2.5 py-1 rounded-full">Save {item.save}%</div>
              {isHourly && (
                <div className="absolute top-3 right-3 bg-[#2E7D32] text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Hourly service
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {highlights.map((h) => (
                <div key={h.label} className="bg-white border border-neutral-200 rounded-xl p-3 text-center">
                  <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto">
                    <h.icon className="w-4 h-4 text-[#2E7D32]" />
                  </div>
                  <p className="mt-2 text-xs font-bold text-neutral-900">{h.label}</p>
                  <p className="text-[10px] text-neutral-500 mt-0.5 leading-tight">{h.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <div className="border-b border-neutral-200 flex">
                {['overview', 'included', 'reviews'].map((t) => (
                  <button key={t} onClick={() => setTab(t)}
                    className={`px-4 py-3 text-sm font-semibold capitalize transition-colors ${tab === t ? 'text-[#2E7D32] border-b-2 border-[#2E7D32]' : 'text-neutral-600 hover:text-neutral-900'}`}>
                    {t === 'included' ? "What's included" : t}
                  </button>
                ))}
              </div>

              <div className="p-4 md:p-5">
                {tab === 'overview' && (
                  <div>
                    <h2 className="font-bold text-lg mb-2">About this service</h2>
                    <p className="text-sm text-neutral-700 leading-relaxed">{item.desc}</p>
                    <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
                      Our {item.name.toLowerCase()} is performed by trained and verified professionals using industry-standard supplies. Book in seconds, track your cleaner live, and pay only after the service is complete.
                    </p>
                    <div className="mt-4 pt-4 border-t border-neutral-100 grid grid-cols-2 gap-3 text-sm">
                      <InfoItem label="Duration" value={isHourly ? `${selectedHours} hour${selectedHours > 1 ? 's' : ''}` : '60 – 90 mins'} />
                      <InfoItem label="Team size" value="1 – 2 pros" />
                      <InfoItem label="Availability" value="Same-day slots" />
                      <InfoItem label="Payment" value="Cash / UPI" />
                    </div>
                  </div>
                )}

                {tab === 'included' && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-bold text-sm mb-2 text-[#2E7D32]">✓ What's included</h3>
                      <ul className="space-y-2">
                        {included.map((i, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                            <Check className="w-4 h-4 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                            <span>{i}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-bold text-sm mb-2 text-red-600">× Not included</h3>
                      <ul className="space-y-2">
                        {excluded.map((i, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                            <span className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-500 font-bold text-center">×</span>
                            <span>{i}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {tab === 'reviews' && (
                  <div>
                    <div className="flex items-center gap-3 pb-4 border-b border-neutral-100">
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="text-3xl font-extrabold">{item.rating}</span>
                          <Star className="w-6 h-6 fill-[#F5B301] text-[#F5B301]" />
                        </div>
                        <p className="text-xs text-neutral-500">Based on {item.reviews} reviews</p>
                      </div>
                    </div>
                    <div className="mt-4 space-y-4">
                      {reviews.map((r) => (
                        <div key={r.id} className="pb-4 border-b border-neutral-100 last:border-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-sm">{r.name}</p>
                              <div className="flex items-center gap-0.5 mt-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'fill-[#F5B301] text-[#F5B301]' : 'text-neutral-300'}`} />
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-neutral-500">{r.date}</p>
                          </div>
                          <p className="mt-2 text-sm text-neutral-700">{r.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="md:col-span-2">
            <div className="md:sticky md:top-24 space-y-4">
              <div className="bg-white border border-neutral-200 rounded-xl p-5">
                <h1 className="hidden md:block text-2xl font-extrabold leading-tight">{item.name}</h1>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-3.5 h-3.5 fill-[#F5B301] text-[#F5B301]" />
                  <span className="text-sm font-semibold">{item.rating}</span>
                  <span className="text-xs text-neutral-500">({item.reviews} reviews)</span>
                  <span className="ml-1 text-[10px] font-bold text-[#2E7D32] bg-[#E8F5E9] px-1.5 py-0.5 rounded-full">{item.basis}</span>
                </div>

                {/* HOUR SELECTOR — only for hourly services */}
                {isHourly && (
                  <div className="mt-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-neutral-900 flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-[#2E7D32]" /> Select duration
                      </p>
                      <p className="text-[11px] text-neutral-500">
                        Rate: ₹{isNewHourly ? item.hourly_rate : item.price}/hr
                        {isNewHourly && minHours > 1 && <span> · min {minHours}h</span>}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2" data-testid="hours-picker">
                      {hourChoices.map((h) => {
                        const opt = HOUR_OPTIONS.find((o) => o.hours === h);
                        const price = isNewHourly ? Number(item.hourly_rate) * h : Math.round(item.price * (opt?.factor || h));
                        const save = isNewHourly ? 0 : (opt?.save || 0);
                        const active = effectiveHours === h;
                        return (
                          <button key={h}
                            data-testid={`hours-option-${h}`}
                            onClick={() => setSelectedHours(h)}
                            className={`relative py-2.5 px-2 rounded-lg border text-left transition-all ${active ? 'border-[#2E7D32] bg-[#E8F5E9] shadow-sm' : 'border-neutral-200 hover:border-[#2E7D32]/50'}`}>
                            <p className={`text-xs font-bold ${active ? 'text-[#2E7D32]' : 'text-neutral-700'}`}>{h} hour{h > 1 ? 's' : ''}</p>
                            <p className={`text-sm font-extrabold ${active ? 'text-[#2E7D32]' : 'text-neutral-900'}`}>₹{price}</p>
                            {save > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 bg-[#EA580C] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">-{save}%</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[11px] text-neutral-500">
                      {isNewHourly
                        ? 'Total = hourly rate × selected hours.'
                        : 'Longer bookings get a better per-hour rate.'}
                    </p>
                  </div>
                )}

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold text-[#EA580C]" data-testid="service-final-price">₹{finalPrice}</span>
                  {isHourly ? (
                    <>
                      <span className="text-sm text-neutral-500">for {effectiveHours}h</span>
                      {perHourSavePct > 0 && (
                        <span className="text-xs font-bold text-[#2E7D32] bg-[#E8F5E9] px-2 py-0.5 rounded-full">Save {perHourSavePct}%/hr</span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-neutral-400 line-through">₹{item.oldPrice}</span>
                      <span className="text-xs font-bold text-[#2E7D32] bg-[#E8F5E9] px-2 py-0.5 rounded-full">Save {item.save}%</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-1">Inclusive of GST</p>

                <div className="mt-4">
                  {inCart ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-[#E8F5E9] rounded-lg px-3 py-2">
                        <span className="text-sm font-semibold text-[#2E7D32]">In your cart</span>
                        <div className="flex items-center border border-[#2E7D32] rounded-lg overflow-hidden bg-white">
                          <button onClick={() => updateQty(cartItemId, -1)} className="w-8 h-8 flex items-center justify-center text-[#2E7D32] hover:bg-neutral-50"><Minus className="w-3.5 h-3.5" /></button>
                          <span className="px-3 text-sm font-bold">{inCart.qty}</span>
                          <button onClick={() => updateQty(cartItemId, 1)} className="w-8 h-8 flex items-center justify-center text-[#2E7D32] hover:bg-neutral-50"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      <button onClick={() => nav('/cart')} className="w-full py-3 rounded-xl bg-[#2E7D32] hover:bg-[#276D2A] text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2">
                        <ShoppingCart className="w-4 h-4" /> View Cart & Checkout
                      </button>
                    </div>
                  ) : (
                    <button onClick={handleAdd} className="w-full py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2">
                      Add to cart {isHourly && `• ${selectedHours}h`} • ₹{finalPrice}
                    </button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-neutral-100 space-y-2 text-xs text-neutral-600">
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#2E7D32]" /> 100% Clean24 guarantee</div>
                  <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#2E7D32]" /> Same-day slots available</div>
                  <div className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-[#2E7D32]" /> Verified & trained professionals</div>
                </div>
              </div>

              {relatedItems.length > 0 && (
                <div className="bg-white border border-neutral-200 rounded-xl p-4">
                  <p className="font-bold text-sm mb-3">More in {data.title}</p>
                  <div className="space-y-2">
                    {relatedItems.map((r) => (
                      <button key={r.id} onClick={() => nav(`/services/${categoryId}/${r.id}`)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 transition-colors text-left">
                        <img src={r.image} alt="" className="w-10 h-10 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{r.name}</p>
                          <p className="text-[11px] text-[#EA580C] font-bold">₹{r.price}{r.basis === 'Hourly' ? '/hr' : ''}</p>
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden fixed bottom-16 left-0 right-0 bg-white border-t border-neutral-100 px-4 py-3 z-30 flex items-center gap-3">
        <div>
          <p className="text-xs text-neutral-500">{isHourly ? `${selectedHours}h total` : 'Total'}</p>
          <p className="font-bold text-lg text-[#EA580C]">₹{finalPrice}</p>
        </div>
        {inCart ? (
          <button onClick={() => nav('/cart')} className="ml-auto py-2.5 px-5 rounded-lg bg-[#2E7D32] text-white font-semibold text-sm flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" /> View Cart ({inCart.qty})
          </button>
        ) : (
          <button onClick={handleAdd} className="ml-auto py-2.5 px-5 rounded-lg bg-[#EA580C] text-white font-semibold text-sm">Add to cart</button>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-neutral-500 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-semibold text-neutral-900">{value}</p>
  </div>
);

export default ServiceDetail;
